// CPH-015 — translates engineer-facing `FilingDeadlineDto.scheduleName` strings
// into treasurer-first display labels at the render boundary. Drives the
// masthead chip strip, FilingsPage, FinanceDashboard, and the Reports archive
// per design doc § Copy + `feedback_state_filer_default_not_fec.md`.
//
// The helper is intentionally pure (no React, no I/O) so it can be reused
// across packages and snapshot-tested cheaply. Unknown (rulesetCode,
// scheduleName) pairs fall back to the raw `scheduleName` verbatim — never
// throw, never return empty, never render a "TODO" to the treasurer.
//
// GA / LA / VA mappings stay deliberately empty until their cross-jurisdiction
// tickets (FPD-046 / FPD-047 / FPD-048) trigger; the fallback covers them.

/** Open type — the table looks up known mappings and falls back gracefully. */
export type RulesetCode = 'AL' | 'GA' | 'LA' | 'VA' | string;

/**
 * Per-(rulesetCode, scheduleName) display-label mappings. Exported so the
 * CPH-015 snapshot test can pin the byte-for-byte literals without coupling
 * to a sub-helper. Edits require a ticket; the snapshot in
 * `__tests__/jurisdictionLabels.test.ts` will fail loudly on drift.
 */
export const JURISDICTION_FORM_LABEL_MAPPINGS: Readonly<
  Record<string, Readonly<Record<string, string>>>
> = {
  AL: {
    'Annual Report': 'Campaign Finance Disclosure Report (Annual)',
    'Monthly Pre-Election Report':
      'Campaign Finance Disclosure Report (Monthly Pre-Election)',
    'Weekly Pre-Election Report':
      'Campaign Finance Disclosure Report (Weekly Pre-Election)',
    'Rapid Report ($20,000+)': 'Major Contribution Report (§17-5-8.1)',
  },
};

/**
 * Resolves the treasurer-first display label for a `FilingDeadlineDto` given
 * the campaign's `rulesetCode`. Returns the raw `scheduleName` verbatim when
 * the pair is unmapped — empty `scheduleName` returns empty, so callers that
 * render the result directly never paint a stray "TODO".
 */
export function jurisdictionFormLabel(
  rulesetCode: string,
  scheduleName: string,
): string {
  const mappings = JURISDICTION_FORM_LABEL_MAPPINGS[rulesetCode];
  if (mappings && Object.prototype.hasOwnProperty.call(mappings, scheduleName)) {
    return mappings[scheduleName];
  }
  return scheduleName;
}
